<template>
  <div id="top" class="help-page-shell help-center-page">
    <div class="help-intro">
      <UiPageHeader title="Help" description="Guides for the modules available to your account, plus the latest workspace updates.">
        <template #actions><span class="help-version-chip">Version 10</span></template>
      </UiPageHeader>
    </div>
    <section class="help-tutorials-section" aria-labelledby="help-tutorials-title">
      <div class="help-tutorials-head">
        <div><h2 id="help-tutorials-title">Your tutorials</h2><p>Find the pages and actions available in your workspace.</p></div>
        <span class="help-module-count">{{ guideCards.length }} {{ guideCards.length === 1 ? 'module' : 'modules' }}</span>
      </div>
      <div v-if="guideCards.length" class="help-guide-grid">
        <article v-for="guide in guideCards" :key="guide.key" class="help-guide-card">
          <div class="help-guide-top">
            <span class="help-guide-icon"><i class="material-icons" aria-hidden="true">{{ guide.icon }}</i></span>
            <div><h3>{{ guide.title }}</h3><p>{{ guide.summary }}</p></div>
          </div>
          <div class="help-guide-block">
            <h4>Pages</h4>
            <div class="help-page-list"><span v-for="page in guide.pages" :key="guide.key + '-' + page.path"><b>{{ page.label }}</b><small>{{ page.path }}</small></span></div>
          </div>
          <div class="help-guide-block">
            <h4>What you can do</h4>
            <ul><li v-for="task in guide.functions" :key="task"><i class="material-icons" aria-hidden="true">check</i><span>{{ task }}</span></li></ul>
          </div>
        </article>
      </div>
      <div v-else class="help-empty-state" role="status"><i class="material-icons" aria-hidden="true">info_outline</i><div><h3>No tutorials available yet</h3><p>No module tutorials are available for your current profile.</p></div></div>
    </section>
    <details class="help-update-card">
      <summary class="help-updates-toggle"><span class="help-update-icon"><i class="material-icons" aria-hidden="true">new_releases</i></span><span><strong>What's new in Version 10</strong><small>Release highlights and workspace improvements</small></span><i class="material-icons help-updates-chevron" aria-hidden="true">expand_more</i></summary>
      <div class="help-updates-content">
        <div class="help-major-grid"><article v-for="item in majorUpdates" :key="item.title" class="help-major-item"><span>{{ item.number }}</span><div><h3>{{ item.title }}</h3><p>{{ item.copy }}</p></div></article></div>
        <div class="help-update-list"><h3>Also improved</h3><ul><li v-for="item in otherUpdates" :key="item"><i class="material-icons" aria-hidden="true">check</i><span>{{ item }}</span></li></ul></div>
      </div>
    </details>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import { addModules } from "@/composables/cache/modules";

const majorUpdates = [
  {
    number: "01",
    title: "Installable Mobile and Desktop App Experience",
    copy: "The workspace now feels more at home on phones, tablets, and desktops, with polished installable app icons, splash assets, and top-bar colors.",
  },
  {
    number: "02",
    title: "Cleaner Claims and Authorization Workflows",
    copy: "Tariff lookup, item selection, review, edit, and delete flows now feel calmer and easier to use.",
  },
  {
    number: "03",
    title: "Smarter Mobile and Tablet Experience",
    copy: "Important work areas now use more of the screen, with better search sheets, actions, and responsive cards.",
  },
  {
    number: "04",
    title: "Better Alerts and Notifications",
    copy: "Pre-authorization notifications are more controlled, with less noise and clearer timing for teams.",
  },
  {
    number: "05",
    title: "Stronger Account Safety",
    copy: "Sign-in, password reset, user deletion, and token cleanup now behave more consistently and securely.",
  },
];

const otherUpdates = [
  "Version 10 adds an in-app Help center with permission-aware tutorials.",
  "Tariff cards now support refresh, clear empty states, and searchable tariff previews.",
  "Claim vetting pages now use softer, more scannable review tables and totals.",
  "Provider, client, claim, authorization, invoice, and enrollee surfaces received focused SBA UI cleanup.",
  "Installable app icons, desktop/mobile app colors, splash assets, and deployment reminders were refreshed.",
  "Login, password reset, and first-time password flows now provide clearer user-facing guidance.",
];

const guideLibrary = {
  supervisor: {
    summary: "Review escalated claim work and approve or reject records assigned for supervisor action.",
    pages: [{ label: "All", path: "/supervisor" }],
    functions: ["Review supervisor claim queues", "Open claim batches", "Approve or reject claims with the right decision trail"],
  },
  hod: {
    summary: "Handle HOD-level claim approvals after earlier review stages are complete.",
    pages: [{ label: "All", path: "/hod" }],
    functions: ["Inspect claim approval queues", "Review provider and claim details", "Complete HOD approval decisions"],
  },
  compliance: {
    summary: "Check invoices and claim visibility before finance or final processing.",
    pages: [{ label: "Invoices", path: "/compliance" }],
    functions: ["Filter invoices by client or status", "Open invoice details", "Review compliance-ready invoice records"],
  },
  finance: {
    summary: "Manage invoice payment visibility and claims payment checks.",
    pages: [{ label: "Finance", path: "/finance" }],
    functions: ["Review invoices and claims", "Inspect payment periods", "Confirm records for finance action"],
  },
  enrollee: {
    summary: "Search, filter, and inspect active enrollee records.",
    pages: [{ label: "All", path: "/enrollee" }],
    functions: ["Search by enrollee details", "Filter by scheme, provider, client, status, and duration", "Open enrollee-linked work safely"],
  },
  auth: {
    summary: "Manage pre-authorization requests from providers and HMO-created authorization work.",
    pages: [
      { label: "All", path: "/auth" },
      { label: "Add", path: "/auth/add" },
      { label: "View", path: "/auth/view/:encounterId" },
    ],
    functions: ["Review diagnosis and requested items", "Check benefits, history, and utilization", "Edit, delete, approve, or reject authorization items"],
  },
  claim: {
    summary: "Manage submitted claims, add manual claims, and vet provider claim batches.",
    pages: [
      { label: "All", path: "/claim" },
      { label: "Add", path: "/claim/add" },
      { label: "View", path: "/claim/view/:clusterId" },
    ],
    functions: ["Filter submitted claim batches", "Add claims on behalf of a provider", "Vet, edit, delete, approve, or reject claim items"],
  },
  private: {
    summary: "Manage HMO client records, schemes, policy periods, and enrollee setup.",
    pages: [
      { label: "All", path: "/private" },
      { label: "Add", path: "/private/add" },
      { label: "View", path: "/private/view/:id" },
    ],
    functions: ["Create and view HMO clients", "Renew policy periods", "Manage client-linked records and status"],
  },
  public: {
    summary: "Manage public scheme records and related enrollee setup.",
    pages: [
      { label: "All", path: "/public" },
      { label: "Add", path: "/public/add/:id" },
    ],
    functions: ["Review scheme records", "Add scheme-linked records", "Track public plan setup"],
  },
  retail: {
    summary: "Manage retail client records and their policy details.",
    pages: [
      { label: "All", path: "/retail" },
      { label: "View", path: "/retail/view/:id" },
    ],
    functions: ["Review retail clients", "Open client profiles", "Track retail policy details"],
  },
  tpa: {
    summary: "Manage TPA and retainer clients.",
    pages: [
      { label: "All", path: "/tpa" },
      { label: "Add", path: "/tpa/add" },
      { label: "View", path: "/tpa/view/:id" },
    ],
    functions: ["Create TPA clients", "Open retainer client records", "Review TPA policy setup"],
  },
  client: {
    summary: "Review all clients across HMO, retail, public, and TPA categories.",
    pages: [
      { label: "All", path: "/client" },
      { label: "View", path: "/client/view/:id" },
    ],
    functions: ["Search clients across categories", "Open consolidated client records", "Move from list to detail quickly"],
  },
  provider: {
    summary: "Manage provider records, profiles, and synchronization with oneProvider where available.",
    pages: [
      { label: "All", path: "/provider" },
      { label: "Add", path: "/provider/add" },
      { label: "Sync", path: "/provider/sync" },
      { label: "View", path: "/provider/view/:id" },
    ],
    functions: ["Create and update providers", "Sync provider records", "Review provider profile and access status"],
  },
  tariffMini: {
    summary: "Review and adjust provider tariff items.",
    pages: [
      { label: "All", path: "/tariff-mini" },
      { label: "View", path: "/tariff-mini/view/:id" },
    ],
    functions: ["Find provider tariff sets", "Inspect item prices", "Review tariff changes"],
  },
  tariffPlus: {
    summary: "Review provider tariffs, add single items, and replace tariff records in bulk.",
    pages: [
      { label: "All", path: "/tariff-plus" },
      { label: "View", path: "/tariff-plus/view/:id" },
    ],
    functions: ["Find provider tariff sets", "Adjust item prices", "Add new items", "Replace provider tariffs in bulk"],
  },
  hospital: {
    summary: "View and manage primary care hospital records.",
    pages: [{ label: "All", path: "/hospital" }],
    functions: ["Browse hospitals", "Inspect primary care facility information", "Support client and enrollee hospital review"],
  },
  report: {
    summary: "Review executive, client, scheme, and utilization insights.",
    pages: [{ label: "Insights", path: "/report" }],
    functions: ["Review executive summaries", "Filter client and scheme insights", "Inspect utilization and high-risk trends"],
  },
  advisory: {
    summary: "Review upcoming invoice advisory records.",
    pages: [{ label: "All", path: "/advisory" }],
    functions: ["View upcoming client invoice periods", "Spot delayed records", "Support proactive client follow-up"],
  },
  adder: {
    summary: "Add dependants to active principals.",
    pages: [{ label: "All", path: "/adder" }],
    functions: ["Find active principals", "Add dependant records", "Keep family structures current"],
  },
  bypass: {
    summary: "Add enrollees to monthly lists while bypassing payment where approved.",
    pages: [{ label: "All", path: "/bypass" }],
    functions: ["Upload or add bypass entries", "Review bypassed enrollee records", "Support controlled exceptions"],
  },
  settings: {
    summary: "Manage application setup areas such as modules, users, tariffs, and plans.",
    pages: [{ label: "Settings", path: "/settings" }],
    functions: ["Manage users and module access", "Review setup controls", "Reset user passwords and maintain account access"],
  },
  encounter: {
    summary: "Create and manage provider-side enrollee encounters and pre-authorization requests.",
    pages: [
      { label: "Encounters", path: "/care/encounter" },
      { label: "Add", path: "/care/encounter/add/:encounterId?" },
    ],
    functions: ["Start an enrollee encounter", "Request pre-authorization", "Track HMO responses"],
  },
  billing: {
    summary: "Prepare and review provider claim submissions.",
    pages: [
      { label: "Billings", path: "/care/billing" },
      { label: "View", path: "/care/billing/view/:clusterId" },
    ],
    functions: ["Review billed claim batches", "Inspect submitted items", "Track provider-side claim status"],
  },
  list: {
    summary: "View active enrollees available to the provider.",
    pages: [{ label: "Lists", path: "/care/list" }],
    functions: ["Search active enrollee lists", "Confirm enrollee eligibility", "Support provider front desk checks"],
  },
  users: {
    summary: "View provider profile credentials.",
    pages: [{ label: "User Profiles", path: "/care/users" }],
    functions: ["Review provider user profiles", "Inspect profile credentials", "Support provider account visibility"],
  },
  members: {
    summary: "View client member records.",
    pages: [{ label: "Members", path: "/company/members" }],
    functions: ["Browse members", "Search member records", "Review membership information"],
  },
  invoices: {
    summary: "View client invoices and invoice periods.",
    pages: [{ label: "Invoices", path: "/company/invoices" }],
    functions: ["Review invoice lists", "Open invoice details", "Track client invoice periods"],
  },
  contract: {
    summary: "View HMO contract information.",
    pages: [{ label: "Contract", path: "/company/contract" }],
    functions: ["Read contract details", "Confirm contract terms", "Support client self-service review"],
  },
  healthcare: {
    summary: "View available healthcare facilities for the client.",
    pages: [{ label: "Healthcare Facilities", path: "/company/healthcare" }],
    functions: ["Browse facilities", "Review primary care options", "Support member hospital guidance"],
  },
  profiles: {
    summary: "View company profile credentials.",
    pages: [{ label: "Profiles", path: "/company/profiles" }],
    functions: ["Review profile information", "Inspect account details", "Support company profile visibility"],
  },
  medical: {
    summary: "View available medical facilities.",
    pages: [{ label: "Medical Facilities", path: "/user/medical" }],
    functions: ["Browse hospitals", "Review available care locations", "Support personal care planning"],
  },
  history: {
    summary: "View treatment history.",
    pages: [{ label: "Medical History", path: "/user/history" }],
    functions: ["Review previous hospital visits", "Check treatment records", "Follow personal utilization history"],
  },
  benefits: {
    summary: "View plan benefits.",
    pages: [{ label: "Benefits", path: "/user/benefits" }],
    functions: ["Review covered benefits", "Check plan access", "Understand available care categories"],
  },
  profile: {
    summary: "View profile information.",
    pages: [{ label: "Profile", path: "/user/profile" }],
    functions: ["Review personal profile", "Check account details", "Confirm member information"],
  },
};

export default {
  name: "HelpHome",
  components: { UiPageHeader },
  setup() {
    const store = useStore();
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    onMounted(() => {
      document.title = "Help";
      tabs.value.push({ id: 1, name: "Help", title: "All", icon: null, url: "help", hash: null });
      currTab.value = tabs.value[0];
      home.value = tabs.value[0];
    });

    const handleActive = (tab) => {
      currTab.value = tab;
    };

    const userModules = computed(() =>
      Array.isArray(store.state.user?.modules) ? store.state.user.modules : []
    );

    const guideCards = computed(() => {
      const userType = store.state.user?.type;

      return addModules()
        .filter((module) =>
          module.type === userType &&
          module.to !== "help" &&
          userModules.value.includes(module.to)
        )
        .map((module) => {
          const guide = guideLibrary[module.to] || {
            summary: module.description || "Use this workspace module for your assigned tasks.",
            pages: [{ label: module.title, path: `/${module.to}` }],
            functions: ["Open the module", "Review the available records", "Complete assigned actions"],
          };

          return {
            key: module.to,
            icon: module.icon,
            title: module.title,
            ...guide,
          };
        });
    });

    return {
      home,
      tabs,
      currTab,
      handleActive,
      guideCards,
      majorUpdates,
      otherUpdates,
    };
  },
};
</script>

<style scoped>
@layer components {
  .help-page-shell.help-center-page { box-sizing: border-box !important; color: var(--ui-foreground) !important; margin: 0 auto !important; max-width: var(--ui-content-width) !important; padding: 2rem 2rem 3rem !important; position: static !important; width: 100% !important; }
  .help-center-page .help-intro { border-bottom: 1px solid var(--ui-border) !important; margin-bottom: 2rem !important; padding-bottom: 1.75rem !important; }
  .help-intro :deep(.ui-page-header) { margin: 0 !important; }
  .help-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem) !important; font-weight: 700 !important; letter-spacing: -.025em !important; }
  .help-intro :deep(.ui-page-header__description) { font-size: 1rem !important; line-height: 1.55 !important; }
  .help-center-page .help-version-chip, .help-module-count { background: var(--ui-secondary) !important; border: 1px solid var(--ui-border) !important; border-radius: 999px !important; color: var(--ui-muted-foreground) !important; display: inline-flex !important; font-size: .75rem !important; font-weight: 600 !important; padding: .4rem .7rem !important; white-space: nowrap !important; }
  .help-center-page .help-tutorials-section { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
  .help-tutorials-head { align-items: center !important; display: flex !important; gap: 1rem !important; justify-content: space-between !important; margin-bottom: 1.25rem !important; }
  .help-center-page h2 { color: var(--ui-foreground) !important; font-size: 1.125rem !important; font-weight: 650 !important; line-height: 1.4 !important; margin: 0 !important; }
  .help-tutorials-head p { color: var(--ui-muted-foreground) !important; font-size: .8125rem !important; line-height: 1.5 !important; margin: .3rem 0 0 !important; }
  .help-guide-grid { display: grid !important; gap: 1.25rem !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .help-center-page .help-guide-card { background: var(--ui-card) !important; border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: none !important; min-width: 0 !important; padding: 1.25rem !important; }
  .help-guide-top { align-items: start !important; display: grid !important; gap: .85rem !important; grid-template-columns: 2.5rem minmax(0, 1fr) !important; }
  .help-center-page .help-guide-icon, .help-update-icon { align-items: center !important; background: var(--ui-muted) !important; border-radius: calc(var(--ui-radius) - 2px) !important; color: var(--ui-muted-foreground) !important; display: inline-flex !important; height: 2.5rem !important; justify-content: center !important; width: 2.5rem !important; }
  .help-guide-icon i, .help-update-icon i { font-size: 1.125rem !important; }
  .help-center-page h3 { color: var(--ui-foreground) !important; font-size: .875rem !important; font-weight: 600 !important; line-height: 1.45 !important; margin: 0 !important; }
  .help-guide-card p, .help-major-item p { color: var(--ui-muted-foreground) !important; font-size: .8125rem !important; line-height: 1.6 !important; margin: .35rem 0 0 !important; }
  .help-guide-block { border-top: 1px solid var(--ui-border) !important; margin-top: 1rem !important; padding-top: 1rem !important; }
  .help-guide-block h4 { color: var(--ui-muted-foreground) !important; font-size: .6875rem !important; font-weight: 600 !important; letter-spacing: .03em !important; margin: 0 0 .65rem !important; text-transform: uppercase !important; }
  .help-page-list { display: flex !important; flex-wrap: wrap !important; gap: .5rem !important; }
  .help-page-list > span { background: var(--ui-muted) !important; border: 1px solid var(--ui-border) !important; border-radius: calc(var(--ui-radius) - 2px) !important; display: flex !important; flex-direction: column !important; gap: .2rem !important; max-width: 100% !important; padding: .5rem .65rem !important; }
  .help-page-list b { color: var(--ui-foreground) !important; font-size: .75rem !important; font-weight: 500 !important; }
  .help-page-list small { color: var(--ui-muted-foreground) !important; font-size: .6875rem !important; overflow-wrap: anywhere !important; }
  .help-center-page ul { list-style: none !important; margin: 0 !important; padding: 0 !important; }
  .help-guide-block li, .help-update-list li { align-items: start !important; color: var(--ui-foreground) !important; display: flex !important; font-size: .8125rem !important; gap: .5rem !important; line-height: 1.6 !important; margin: 0 0 .5rem !important; }
  .help-guide-block li i, .help-update-list li i { color: #0f766e !important; flex: 0 0 auto !important; font-size: 1rem !important; margin-top: .15rem !important; }
  .help-center-page .help-update-card { background: var(--ui-card) !important; border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: none !important; margin: 2rem 0 0 !important; padding: 0 !important; }
  .help-updates-toggle { align-items: center !important; cursor: pointer !important; display: flex !important; gap: .85rem !important; list-style: none !important; padding: 1.25rem !important; }
  .help-updates-toggle::-webkit-details-marker { display: none !important; }
  .help-updates-toggle strong { color: var(--ui-foreground) !important; display: block !important; font-size: .875rem !important; font-weight: 600 !important; }
  .help-updates-toggle small { color: var(--ui-muted-foreground) !important; display: block !important; font-size: .75rem !important; margin-top: .25rem !important; }
  .help-updates-chevron { color: var(--ui-muted-foreground) !important; font-size: 1.25rem !important; margin-left: auto !important; }
  .help-update-card[open] .help-updates-chevron { transform: rotate(180deg) !important; }
  .help-updates-toggle:focus-visible { border-radius: var(--ui-radius) !important; outline: 2px solid var(--ui-ring) !important; outline-offset: 2px !important; }
  .help-updates-content { border-top: 1px solid var(--ui-border) !important; padding: 1.25rem !important; }
  .help-major-grid { display: grid !important; gap: 1.25rem !important; grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
  .help-major-item { display: flex !important; gap: .75rem !important; min-width: 0 !important; }
  .help-major-item > span { color: #0f766e !important; font-size: .75rem !important; font-weight: 600 !important; padding-top: .15rem !important; }
  .help-update-list { border-top: 1px solid var(--ui-border) !important; margin-top: 1.25rem !important; padding-top: 1.25rem !important; }
  .help-update-list ul { margin-top: .75rem !important; }
  .help-empty-state { align-items: start !important; border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; display: flex !important; gap: .75rem !important; padding: 1.5rem !important; }
  .help-empty-state > i { color: var(--ui-muted-foreground) !important; font-size: 1.25rem !important; }
  .help-empty-state p { color: var(--ui-muted-foreground) !important; font-size: .8125rem !important; margin: .35rem 0 0 !important; }
  @media (max-width: 1000px) { .help-major-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
  @media (max-width: 700px) {
    .help-page-shell.help-center-page { padding: 1.25rem 1rem 2.5rem !important; }
    .help-center-page .help-intro { margin-bottom: 1.5rem !important; padding-bottom: 1.25rem !important; }
    .help-guide-grid, .help-major-grid { grid-template-columns: minmax(0, 1fr) !important; }
    .help-tutorials-head { align-items: start !important; }
  }
}
</style>
